/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react'
import { postRequestOptions } from './Fetchwrapper'

export interface PlanningFilters {
	idProjects: number | ''
	asOfDate: string
	method: number
	horizon: number
	safetyDays: number
	pendingDays: number
	poDays: number
}

export interface PlanningTabProps {
	active: boolean
	filters: PlanningFilters
}

export const methodOptions = [
	{ label: 'Average of last 3 months', value: 3 },
	{ label: 'Average of last 6 months', value: 6 },
	{ label: 'Average of last 12 months', value: 12 },
]

export const horizonOptions = [
	{ label: 'Next 30 days', value: 30 },
	{ label: 'Next 60 days', value: 60 },
	{ label: 'Next 90 days', value: 90 },
]

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const toNumber = (value: any) => {
	const num = parseFloat(value)
	return isNaN(num) ? 0 : num
}

/** A quantity with thousands separators and at most `decimals` decimals; blank for nothing. */
export const quantity = (value: any, decimals = 0) => {
	if (value === null || value === undefined || value === '') {
		return ''
	}
	return toNumber(value).toLocaleString('en-US', { maximumFractionDigits: decimals })
}

export const toInputDate = (date: Date) => {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

export const toInputMonth = (date: Date) => toInputDate(date).slice(0, 7)

/** '2026-10' -> 'Oct 2026' */
export const monthLabel = (month: string) => {
	const [year, monthNo] = month.split('-')
	return `${monthNames[parseInt(monthNo, 10) - 1] || ''} ${year}`
}

/** '2026-10-13' -> '13 Oct 2026' */
export const dateLabel = (date: string | null | undefined) => {
	if (!date) {
		return ''
	}
	const [year, monthNo, day] = date.slice(0, 10).split('-')
	return `${parseInt(day, 10)} ${monthNames[parseInt(monthNo, 10) - 1] || ''} ${year}`
}

/** Which months a plan period draws its forecast from, and whether each is a saved or a calculated one. */
export const forecastSources = (months: { month: string; days: number; saved: boolean }[]) =>
	months.map((month) => `${monthLabel(month.month)} (${month.days} days, ${month.saved ? 'saved forecast' : 'calculated'})`).join(', ')

export const matchesSearch = (search: string, ...values: any[]) => {
	const needle = search.trim().toLowerCase()
	return needle === '' || values.some((value) => String(value ?? '').toLowerCase().includes(needle))
}

export const downloadCsv = (fileName: string, header: string[], lines: any[][]) => {
	const csv = [header].concat(lines)
		.map((line) => line.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
		.join('\r\n')
	const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
	const link = document.createElement('a')
	link.href = URL.createObjectURL(blob)
	link.download = fileName
	link.click()
	URL.revokeObjectURL(link.href)
}

/**
 * Posts to a Production Planning endpoint.
 * Resolves with the response body; `errors` holds the messages when it did not succeed.
 */
export const postPlanning = async (path: string, body: any): Promise<{ success: boolean; data?: any; successMessage?: string; errors: string[] }> => {
	try {
		const response = await fetch(process.env.REACT_APP_API_URL + 'production-planning/' + path, postRequestOptions(body))
		const json = await response.json()
		if (json.success) {
			return { success: true, data: json.data, successMessage: json.successMessage, errors: [] }
		}

		const errors: string[] = []
		Object.values(json.errors || {}).forEach((messages: any) => {
			errors.push(Array.isArray(messages) ? messages.join(' ') : String(messages))
		})
		return { success: false, errors: errors.length ? errors : [json.message || 'The report could not be loaded.'] }
	} catch (error) {
		console.error('Production planning request failed:', error)
		return { success: false, errors: ['The report could not be loaded. Check the connection and try again.'] }
	}
}

/**
 * Loads a Production Planning report while its tab is open, again whenever `body` changes.
 * Pass `body` as null until the report can be asked for (no project chosen yet).
 */
export const usePlanningReport = (active: boolean, path: string, body: any) => {
	const [state, setState] = useState<{ loading: boolean; data: any; errors: string[] }>({ loading: false, data: null, errors: [] })
	const [reloads, setReloads] = useState(0)
	const loadedKey = useRef('')
	const key = body ? path + JSON.stringify(body) + reloads : ''

	useEffect(() => {
		if (!active || key === '' || loadedKey.current === key) {
			return
		}

		let finished = false
		loadedKey.current = key
		setState((current) => ({ ...current, loading: true }))
		postPlanning(path, body).then((result) => {
			if (finished) {
				return
			}
			finished = true
			setState({ loading: false, data: result.success ? result.data : null, errors: result.errors })
		})

		return () => {
			// left before the answer came: ask again next time
			if (!finished) {
				finished = true
				loadedKey.current = ''
			}
		}
	}, [active, key])

	return { ...state, reload: () => setReloads((count) => count + 1) }
}
